import { DateTime } from "luxon";

/**
 * Parse an API timestamp the same way Helpers.EST does: ISO without offset/Z is UTC.
 */
function parseApiDateTime(value: string | Date | null | undefined): DateTime | null {
  if (value == null || value === "") return null;

  if (value instanceof Date) {
    const dt = DateTime.fromJSDate(value, { zone: "utc" });
    return dt.isValid ? dt : null;
  }

  const raw = String(value).trim();
  if (!raw) return null;

  const hasZone = /([zZ]|[+-]\d{2}:?\d{2})$/.test(raw);
  const dt = hasZone
    ? DateTime.fromISO(raw, { setZone: true })
    : DateTime.fromISO(raw, { zone: "utc" });

  return dt.isValid ? dt : null;
}

/**
 * Elapsed time from assessment StartTime to now as dd:hh:mm (days:hours:minutes).
 * Uses the same UTC convention as Helpers.EST / dashboard Start Time.
 */
export function formatElapsedDdHhMm(
  start: string | Date | null | undefined,
  now: Date | number | DateTime = Date.now(),
) {
  const startDt = parseApiDateTime(start);
  if (!startDt) return "";

  let endDt: DateTime;
  if (DateTime.isDateTime(now)) {
    endDt = now;
  } else if (typeof now === "number") {
    endDt = DateTime.fromMillis(now, { zone: "utc" });
  } else if (now instanceof Date) {
    endDt = DateTime.fromJSDate(now, { zone: "utc" });
  } else {
    endDt = DateTime.utc();
  }

  if (!endDt.isValid) return "";

  const totalSeconds = Math.max(
    0,
    Math.floor(endDt.toUTC().diff(startDt.toUTC(), "seconds").seconds),
  );
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);

  return [
    String(days).padStart(2, "0"),
    String(hours).padStart(2, "0"),
    String(minutes).padStart(2, "0"),
  ].join(":");
}
